import React from 'react';
import { useAtomValue } from 'jotai';
import { CarouselSection } from '@rocco/ui/layout/components/CarouselSection';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';
import type {
    TileableModel,
    TileViewModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { searchPageDataAtom } from '../states/search-page-data-atom';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

/**
 * Renders a carousel section displaying recently viewed items.
 * Adapts tile aspect ratio based on mobile/desktop view.
 */
export const SearchPageRecentlyViewed: React.FC = () => {
    const { isInDesignBoard, handleFollow } = useHandleFollow();
    const pageDataFromAtom = useAtomValue(searchPageDataAtom);
    const recentlyViewedItemsFromData =
        pageDataFromAtom?.recentlyViewedItems ?? [];
    const appDisplayMode = pageDataFromAtom?.appDisplayMode;

    const isMobile = appDisplayMode === 'mobile';

    if (recentlyViewedItemsFromData.length === 0) {
        return null;
    }

    const recentlyViewedTiles: TileViewModel<TileableModel>[] =
        recentlyViewedItemsFromData.map((item: TileableModel) => {
            return {
                aspectRatio: isMobile ? 'default' : 'square',
                model: item,
            };
        });

    return (
        <CarouselSection
            title="Recently viewed"
            size={isMobile ? 'mobileSearchResults' : 'recentlyViewed'}
        >
            {recentlyViewedTiles.map((tile, index) => (
                <Tile
                    key={`recentlyViewedTiles-${index}`}
                    tile={{
                        ...tile,
                        isFollowed: isInDesignBoard(tile.model.id),
                        onFollow: (pinId, pinType) => {
                            return handleFollow({ id: pinId }, pinType);
                        },
                    }}
                    showTypeBadge={true}
                />
            ))}
        </CarouselSection>
    );
};
